import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, inr } from "../../lib/api";
import { useCart } from "../../context/CartContext";
import { Gift, Heart, Sparkles, ArrowRight, Star, ShoppingBag, ChevronRight, Lock } from "lucide-react";
import { toast } from "sonner";

export default function SharedWishlist() {
  const { token } = useParams();
  const { add, setOpen } = useCart();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    api.get(`/wishlist/shared/${token}`)
      .then(r => setData(r.data))
      .catch(e => setError(e.response?.data?.detail || "Wishlist not found"))
      .finally(() => setLoading(false));
  }, [token]);

  const buyForThem = (p) => {
    const v = p.variants?.[0];
    if (!v) return;
    add(p, v, 1);
    toast.success(`Added ${p.name} — buy it as a gift!`);
    setOpen(true);
  };

  if (loading) return <div className="py-24 text-center text-stone-500">Loading gift list…</div>;

  if (error) return (
    <div className="max-w-md mx-auto px-4 py-24 text-center relative">
      <div className="blob blob-terra" style={{ width: 400, height: 400, top: 50, left: "50%", transform: "translateX(-50%)" }}/>
      <div className="relative">
        <div className="w-20 h-20 rounded-full bg-[#F5DEC7] flex items-center justify-center mx-auto mb-4">
          <Lock className="w-10 h-10 text-[#993322]"/>
        </div>
        <h1 className="font-serif-display text-4xl">This wishlist is private</h1>
        <p className="text-stone-500 mt-3">{error}</p>
        <Link to="/shop" className="inline-flex items-center gap-2 mt-6 bg-[#3B2F2F] text-white rounded-full px-6 py-3 hover:bg-black">
          Browse the shop <ChevronRight className="w-4 h-4"/>
        </Link>
      </div>
    </div>
  );

  return (
    <div className="relative">
      <div className="blob blob-gold" style={{ width: 500, height: 500, top: -50, right: -180 }}/>
      <div className="blob blob-terra" style={{ width: 400, height: 400, top: 400, left: -140 }}/>

      <div className="max-w-6xl mx-auto px-4 md:px-8 py-12 relative z-10">
        {/* Hero */}
        <div className="text-center max-w-2xl mx-auto mb-12">
          <div className="inline-flex items-center gap-2 bg-white/70 backdrop-blur border border-[#D4AF37]/40 rounded-full px-4 py-1.5 mb-6">
            <Gift className="w-3.5 h-3.5 text-[#D4AF37]"/>
            <span className="uppercase text-[10px] tracking-[0.25em] text-[#8B7355] font-semibold">Gift ideas</span>
          </div>
          <div className="relative w-20 h-20 mx-auto mb-6">
            <div className="absolute inset-0 rounded-full bg-[#D4AF37]/20 animate-ping"/>
            <div className="relative w-20 h-20 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#B8942C] flex items-center justify-center shadow-2xl shadow-[#D4AF37]/40">
              <Heart className="w-10 h-10 text-white fill-white"/>
            </div>
          </div>
          <h1 className="font-serif-display text-5xl md:text-6xl leading-[0.95]">
            <span className="italic text-[#8B7355]">{data.wisher_name}</span>'s<br/>coffee wishlist
          </h1>
          <p className="text-stone-600 mt-5 text-lg leading-relaxed">
            {data.count === 0
              ? "This list is empty for now — check back soon!"
              : `Here are the ${data.count} coffee${data.count!==1?"s":""} on their radar. Perfect for gifting a warm surprise.`}
          </p>
          {data.share_message && (
            <div className="mt-6 max-w-lg mx-auto bg-white/80 backdrop-blur border border-stone-200 rounded-2xl p-4 text-sm text-stone-700 italic">
              &ldquo;{data.share_message}&rdquo; <span className="not-italic text-xs text-stone-500 block mt-1">— {data.wisher_name}</span>
            </div>
          )}
        </div>

        {/* Products */}
        {data.products.length === 0 ? (
          <div className="text-center max-w-md mx-auto">
            <Link to="/shop" className="inline-flex items-center gap-2 bg-[#3B2F2F] text-white rounded-full px-6 py-3 hover:bg-black">
              Browse products anyway <ArrowRight className="w-4 h-4"/>
            </Link>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-6">
            {data.products.map((p, i) => (
              <div key={p.product_id} className="bg-white border border-stone-100 rounded-3xl overflow-hidden group hover:border-[#D4AF37]/40 hover:shadow-xl transition-all relative"
                   data-testid={`shared-product-${p.slug}`}>
                <div className="absolute top-3 left-3 z-10 bg-[#D4AF37] text-[#3B2F2F] text-[10px] font-outfit font-bold uppercase tracking-widest px-2 py-1 rounded-full shadow flex items-center gap-1">
                  <Heart className="w-3 h-3 fill-current"/> Gift idea #{i+1}
                </div>
                <Link to={`/product/${p.slug}`} className="block">
                  <div className="aspect-square overflow-hidden bg-gradient-to-br from-[#F5F0E6] to-[#FEF3E2]">
                    <img src={p.images?.[0]} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"/>
                  </div>
                </Link>
                <div className="p-5">
                  <div className="text-[10px] uppercase tracking-widest text-[#8B7355]">{p.category_slug?.replace(/-/g," ")}</div>
                  <Link to={`/product/${p.slug}`} className="block">
                    <div className="font-serif-display text-xl mt-1 leading-tight min-h-[3rem] hover:text-[#3B2F2F]">{p.name}</div>
                  </Link>
                  <div className="flex items-center gap-2 mt-2">
                    <Star className="w-3.5 h-3.5 fill-[#D4AF37] text-[#D4AF37]"/>
                    <span className="text-xs text-stone-500 font-outfit">{p.rating} · {p.review_count || 0} reviews</span>
                  </div>
                  <div className="mt-4 pt-4 border-t border-stone-100 flex items-center justify-between">
                    <div>
                      <div className="text-[10px] text-stone-400 uppercase tracking-widest">From</div>
                      <div className="font-outfit font-bold text-[#3B2F2F]">{inr(p.min_price)}</div>
                    </div>
                    <button onClick={() => buyForThem(p)} data-testid={`gift-${p.slug}`}
                            className="bg-[#3B2F2F] text-white rounded-full px-4 py-2 text-xs font-medium hover:bg-black inline-flex items-center gap-1.5">
                      <ShoppingBag className="w-3.5 h-3.5"/> Gift it
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Footer */}
        <div className="mt-16 bg-gradient-to-br from-[#3B2F2F] to-[#2A2020] text-[#FDFBF7] rounded-3xl p-8 md:p-10 relative overflow-hidden">
          <div className="pattern-beans absolute inset-0 opacity-20"/>
          <div className="relative flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <div className="uppercase text-[10px] tracking-[0.3em] text-[#D4AF37] mb-2">Start your own list</div>
              <h3 className="font-serif-display text-3xl md:text-4xl">Fancy your own coffee wishlist?</h3>
              <p className="text-sm text-[#FDFBF7]/70 mt-2 max-w-md">Save what you love and share your list with friends and family. Perfect for birthdays and festive season.</p>
            </div>
            <Link to="/shop" className="inline-flex items-center gap-2 bg-[#D4AF37] text-[#3B2F2F] rounded-full px-6 py-3 hover:bg-[#E8C87C] font-semibold shrink-0">
              Browse coffee <Sparkles className="w-4 h-4"/>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
